import React, { useCallback, useEffect, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useReactFlow } from '@xyflow/react';
import isEqual from 'fast-deep-equal';
import { useForm } from 'react-hook-form';

import { logger } from '@/utils/logger';

import { useNodeContext } from '../../context/node-context';
import { useNodeNames } from '../../context/node-names.context';
import { useCustomReactFlowState } from '../../context/reactflow-context';
import { emitRecipeModified } from '../../events/recipeEditorEvents';
import { useModalCloser } from '../../hooks/useModalCloser';
import {
  transformFormDataToNodeData,
  transformStatementFormDataToNodeData,
} from '../../nodes/transformer';
import { nodeUtils } from '../../utils/nodeUtils';

/**
 * Converts empty values to boolean false, otherwise returns the original value
 * @param {*} value - The value to check and potentially convert
 * @returns {boolean|*} Returns false for empty arrays, objects, strings, null or undefined.
 *                      Otherwise returns the original value
 * @example
 * convertToBooleanIfEmpty([]) // returns false
 * convertToBooleanIfEmpty({}) // returns false
 * convertToBooleanIfEmpty("  ") // returns false
 * convertToBooleanIfEmpty(null) // returns false
 * convertToBooleanIfEmpty([1,2]) // returns [1,2]
 * convertToBooleanIfEmpty({a:1}) // returns {a:1}
 * convertToBooleanIfEmpty("test") // returns "test"
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Utility function handles multiple types
const convertToBooleanIfEmpty = (value: any): any => {
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    return value;
  } else if (typeof value === 'object') {
    if (Object.keys(value).length === 0) return false;
    return value;
  } else if (typeof value === 'string') {
    if (value.trim() === '') return false;
    return value;
  } else if (value === undefined || value === null) {
    return false;
  }
  return value;
};

interface NodeFormProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form values are dynamic based on node type
  defaultValues: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form data is dynamic based on node type
  onSubmit?: (data: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form data is dynamic based on node type
  onChange?: (data: any) => void;
  showModal: boolean;
  onHide?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Schema resolver from Yup is dynamic
  schemaResolver: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form component props are dynamic
  FormComponent: React.ComponentType<any>;
}

export const NodeForm: React.FC<NodeFormProps> = ({
  defaultValues,
  onSubmit = () => {},
  onChange = () => {},
  showModal,
  onHide = () => {},
  schemaResolver,
  FormComponent,
}) => {
  const { setNodeError, isReadOnly } = useCustomReactFlowState();
  const { setOrRenameNodeName } = useNodeNames();
  const { updateNodeData, getNodes, setEdges, getEdges } = useReactFlow();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form values are dynamic based on node type
  const initialFormValuesRef = useRef<any>(null);
  // updateNodeDataInCurrentContext this is required because we are using current node context to consume data in the other parts of node components and as an initial state the data is being provided to the node context so to update the state data this needs to be invoked this has nothing to do with the node updates of the reactflow
  const { node, updateNodeData: updateNodeDataInCurrentContext } = useNodeContext();
  const { id, metaData } = node;

  // Create a custom resolver that conditionally applies the yup schema
  const conditionalResolver = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup resolver types are dynamic
    async (data: any, context: any, options: any) => {
      // If in read-only mode, return the data as valid without validation
      if (isReadOnly) {
        // Return all fields as valid
        return {
          values: data,
          errors: {},
        };
      }

      // Otherwise, use the yup resolver
      return yupResolver(schemaResolver)(data, context, options);
    },
    [isReadOnly, schemaResolver]
  );

  const form = useForm({
    defaultValues,
    shouldFocusError: false,
    mode: 'all',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Custom resolver requires type assertion
    resolver: conditionalResolver as any,
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
    initialFormValuesRef.current = form.getValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Run only once on mount
  }, []);

  useEffect(() => {
    if (showModal && defaultValues) {
      form.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.reset is stable, defaultValues checked inside effect
  }, [showModal]);

  const handleCloseModal = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Event type is dynamic
    async (event?: any) => {
      event?.stopPropagation();
      if (isReadOnly) {
        return onHide();
      }
      const isValid = await form.trigger();
      const values = form.getValues();
      const checkForSchemaChanges = () => {
        const initialValuesSchema = initialFormValuesRef.current?.schema;
        const currentValuesSchema = values.schema;

        const hasChanges = !isEqual(
          convertToBooleanIfEmpty(initialValuesSchema),
          convertToBooleanIfEmpty(currentValuesSchema)
        );
        return hasChanges;
      };
      const hasSchemaChanges = checkForSchemaChanges();
      if (!isValid || Object.keys(form.formState.dirtyFields).length > 0 || hasSchemaChanges) {
        const shouldClose = window.confirm(
          'There are unsaved changes. Are you sure you want to close?'
        );
        if (shouldClose) {
          form.reset(defaultValues);
          onHide();
        }
        setTimeout(() => {
          form.trigger().then((isValid) => setNodeError(id, !isValid));
        }, 300);
      } else {
        onHide();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id, isReadOnly, setNodeError are stable
    [form, onHide, defaultValues]
  );

  useModalCloser(id, showModal, handleCloseModal);

  const handleNameChangeSideEffects = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form data is dynamic based on node type
    (data: any) => {
      if (isReadOnly) return;
      try {
        // Pass the actual node type, not the category
        // For dataframes, use the "data-frame" field instead of "name"
        const nodeName =
          nodeUtils.getNodeType(node) === 'dataframe'
            ? data?.['data-frame'] || ''
            : data?.name || '';

        setOrRenameNodeName(id, nodeName, node.type);
      } catch (err) {
        logger.error('Error in setOrRenameNodeName:', err);
        throw err;
      }

      try {
        nodeUtils.updateQueriesOnNodeNameChangeAndUpdateEdges({
          getNodes,
          getEdges,
          id,
          data,
          updateNodeData,
          updateNodeDataInCurrentContext,
          setEdges,
        });
      } catch (error) {
        logger.error('Error in updateQueriesOnNodeNameChange:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getEdges and setEdges are stable from ReactFlow
    [id, setOrRenameNodeName, getNodes, updateNodeData, updateNodeDataInCurrentContext, node]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form data type is dynamic based on node type
  const handleFormSubmit = (data: any) => {
    if (isReadOnly) return;
    try {
      handleNameChangeSideEffects(data);
    } catch (error: unknown) {
      logger.error('Error setting node name:', error);
      const errMsg = error instanceof Error ? error.message : 'Unknown error';
      if (nodeUtils.getNodeType(node) === 'dataframe') {
        form.setError('data-frame', { message: errMsg });
      } else {
        form.setError('name', { message: errMsg });
      }

      return; // Stop execution here to prevent form submission
    }
    const nodeData =
      nodeUtils.getNodeType(node) === 'dataframe' || nodeUtils.getNodeType(node) === 'command'
        ? transformStatementFormDataToNodeData(data)
        : transformFormDataToNodeData(data);

    updateNodeData(id, nodeData);
    updateNodeDataInCurrentContext(nodeData);

    // Emit event: node properties updated.
    // Use force:true to bypass the ignoreNextModifications counter which is
    // designed for ReactFlow's internal change events, not explicit user saves.
    emitRecipeModified({ force: true });

    const newDefaultValues = { ...data };
    form.reset(newDefaultValues);
    initialFormValuesRef.current = form.getValues();
    onSubmit?.(data);
    onHide?.();
    setNodeError(id, false);
  };

  return (
    <Dialog
      open={showModal}
      onClose={handleCloseModal}
      maxWidth="lg"
      fullWidth
      // Prevent backdrop click from closing when there are changes
      onBackdropClick={(e) => {
        e.stopPropagation();
        handleCloseModal(e);
      }}
    >
      <DialogTitle>
        {typeof metaData?.modalTitle === 'object'
          ? metaData?.modalTitle?.['#text'] || 'Node Details'
          : metaData?.modalTitle}
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="p-3" style={{ padding: '30px' }}>
          <FormComponent form={form} onSubmit={handleFormSubmit} onChange={() => onChange} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
